import clsx from "clsx";
import React, { useEffect, useMemo, useRef } from "react";
import { isMobile } from "react-device-detect";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink } from "react-router-dom";

import { useGetChatThreadsQuery } from "~/api";
import { Banners } from "~/components/promotion/banners";
import { Badge } from "~/components/ui/badge";
import { CLINICAL_CASE_LIBRARY_URL } from "~/config";
import { filterVisibleChatThreads } from "~/features/chat/chat-thread-visibility";
import { usePaymentsCount, useShipmentsCount, useUnpaidEventsCount } from "~/hooks/use-user";
import { getBanners, selectBanners } from "~/slices/banners";
import { selectUser } from "~/slices/user";
import { useAppDispatch, useAppSelector } from "~/store";

import { ManagerInfo } from "./manager_info";
import { AcademicCapIcon, TruckIcon, WalletIcon } from "./profile_menu";

/** Matches layout4 light theme: sub-menu icons #a7bdcd, hover/active #5b9bd1. */
const patientSubMenuIconClass = "tw-shrink-0 tw-text-[#a7bdcd] tw-transition-colors group-hover:tw-text-[#5b9bd1]";

const patientSubMenuLinkActiveClass = "[&_i[class^='icon-']]:!tw-text-[#5b9bd1] [&_svg]:!tw-text-[#5b9bd1]";

function PatientSubMenuIcon({ name }: { name: string }) {
  return <i aria-hidden className={clsx(name, patientSubMenuIconClass)} />;
}

export function PatientMenu() {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const banners = useAppSelector(selectBanners);
  const user = useAppSelector(selectUser);
  const bannersLeft = banners.filter((banner) => banner.align == "left");

  const menuRef = useRef<HTMLUListElement>(null);

  const numberOfPayments = usePaymentsCount();
  const numberOfShipments = useShipmentsCount();
  const numberOfUnpaidEvents = useUnpaidEventsCount();
  const { data: chatThreadsData } = useGetChatThreadsQuery(undefined, { pollingInterval: 5000 });
  const visibleChatThreads = useMemo(() => filterVisibleChatThreads(chatThreadsData?.threads ?? []), [chatThreadsData?.threads]);
  const chatUnread = visibleChatThreads.reduce((sum, t) => sum + (t.unread_count ?? 0), 0);

  useEffect(() => {
    void dispatch(getBanners());
    const pathname = window.location.pathname;

    menuRef.current?.querySelectorAll("a").forEach((anchorEl) => {
      if (anchorEl.getAttribute("href") == pathname) {
        anchorEl.closest("li")?.classList.add("active");
      }
    });
  }, [dispatch]);

  return (
    <div className="page-sidebar-wrapper max-sm:tw-mb-8">
      <aside
        id="patient-menu-sidebar"
        className={clsx(
          "page-sidebar",
          "navbar-collapse collapse",
          ["/pages/patients", "pages/home"].some((loc) => window.location.pathname == loc) ? "in" : null,
          "w-64 shrink-0 border-r border-gray-200",
        )}
      >
        <ManagerInfo manager={user.manager ? user.manager : null} />

        <ul ref={menuRef} className="page-sidebar-menu" data-keep-expanded="false" data-auto-scroll="true" data-slide-speed={200}>
          <li className="nav-item start active">
            <a href="/pages/patients" className="nav-link nav-toggle focus-visible:tw-ring-2 focus-visible:tw-ring-blue-600" id="sidebar" onClick={(event) => event.preventDefault()}>
              <i aria-hidden className="icon-home" />
              <FormattedMessage id="left.menu.header" />
            </a>

            <ul className="sub-menu" id="sidebar-submenu">
              <PatientMenuItem id="sidebar-patients" to="/pages/patients" icon={<PatientSubMenuIcon name="icon-users" />} title={<FormattedMessage id="left.menu.patients" />} />

              <PatientMenuItem
                to="/pages/shipments"
                icon={<TruckIcon className={clsx("tw-size-5 tw-align-bottom", patientSubMenuIconClass)} />}
                title={<FormattedMessage id="left.menu.shipments" />}
                badge={numberOfShipments}
              />

              <PatientMenuItem to="/pages/new_patient" id="sidebar-patient-add" icon={<PatientSubMenuIcon name="icon-user-follow" />} title={<FormattedMessage id="left.menu.add_patient" />} />

              {
                <PatientMenuItem
                  to="/pages/payments"
                  icon={<WalletIcon className={clsx("tw-size-5 tw-align-bottom", patientSubMenuIconClass)} />}
                  title={<FormattedMessage id="left.menu.my_payments" />}
                  badge={numberOfPayments}
                />
              }

              <PatientMenuItem
                id="sidebar-chat"
                to="/pages/chat"
                icon={<PatientSubMenuIcon name="icon-bubbles" />}
                title={<FormattedMessage id="left.menu.chat" />}
                badge={chatUnread}
                badgeMax={99}
                badgeSrOnlyText={intl.formatMessage({ id: "chat.unread_total" }, { count: chatUnread })}
              />

              <PatientMenuItem
                to="/pages/events"
                badge={numberOfUnpaidEvents}
                icon={<AcademicCapIcon className={clsx("tw-size-5 tw-align-bottom", patientSubMenuIconClass)} />}
                title={<FormattedMessage id="events" />}
              />

              <PatientMenuItem id="sidebar-meetings" to="/pages/meetings" icon={<PatientSubMenuIcon name="icon-calendar" />} title={<FormattedMessage id="meetings.title" />} />

              <PatientMenuItem id="sidebar-instructions" to="/pages/manuals" icon={<PatientSubMenuIcon name="icon-notebook" />} title={<FormattedMessage id="manuals.title" />} />

              {<PatientMenuItem id="sidebar-calendar" to={CLINICAL_CASE_LIBRARY_URL} icon={<PatientSubMenuIcon name="icon-doc" />} title={<FormattedMessage id="left.menu.library" />} />}

              <PatientMenuItem id="sidebar-promo" to="/pages/promo" icon={<PatientSubMenuIcon name="icon-heart" />} title={<FormattedMessage id="promo.title" />} />
            </ul>
          </li>
        </ul>

        {!isMobile && bannersLeft.length > 0 ? <Banners banners={bannersLeft} /> : null}
      </aside>
    </div>
  );
}

function PatientMenuItem({
  to,
  icon,
  title,
  badge = 0,
  badgeMax = Number.POSITIVE_INFINITY,
  badgeSrOnlyText = "",
  id,
}: {
  to: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  badge?: number;
  /** When set, show "{badgeMax}+" if badge exceeds this (e.g. 99 for chat). */
  badgeMax?: number;
  badgeSrOnlyText?: string;
  id?: string;
}) {
  // Files under /api/* must be opened as regular links (not SPA navigation),
  // otherwise React Router intercepts and you get a "reload" / blank route.
  const isExternalLink = to.startsWith("http") || to.startsWith("/api/");
  const linkClassName = clsx("nav-link tw-group tw-relative tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-pl-6", "focus-visible:tw-ring-2 focus-visible:tw-ring-blue-600");

  const badgeCapped = typeof badgeMax === "number" && badgeMax < Number.POSITIVE_INFINITY && badge > badgeMax ? `${badgeMax}+` : badge;

  const children = (
    <>
      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-overflow-hidden">
        {icon}
        <span className="tw-ml-1 tw-min-w-0 tw-truncate">{title}</span>
      </span>

      {badge > 0 ? (
        <Badge srOnlyText={badgeSrOnlyText} className="tw-shrink-0">
          {badgeCapped}
        </Badge>
      ) : null}
    </>
  );

  function collapsePatientMenuOnMobile() {
    document.getElementById("js-patient-menu-toggle-btn")?.click();
  }

  return (
    <li className="nav-item start" id={id}>
      {isExternalLink ? (
        <a href={to} rel="noopener noreferrer" target="_blank" className={linkClassName}>
          {children}
        </a>
      ) : (
        <NavLink to={to} className={linkClassName} activeClassName={patientSubMenuLinkActiveClass} onClick={isMobile ? collapsePatientMenuOnMobile : undefined}>
          {children}
        </NavLink>
      )}
    </li>
  );
}
